import type { ComponentType } from "react"
import { lazy } from "react"

type LazyFactory<T extends ComponentType<unknown>> = () => Promise<{
  default: T
}>

interface PreloadableComponent<
  T extends ComponentType<unknown>,
> extends React.LazyExoticComponent<T> {
  preload: () => Promise<{ default: T }>
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const msg = error.message
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk")
  )
}

const RELOAD_KEY = "chunk-reload-ts"
const RELOAD_COOLDOWN_MS = 10_000

function reloadIfStale(): never {
  const now = Date.now()
  const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || "0")

  if (Number.isFinite(lastReload) && now - lastReload < RELOAD_COOLDOWN_MS) {
    throw new Error("Page already reloaded due to stale chunks")
  }

  sessionStorage.setItem(RELOAD_KEY, String(now))
  window.location.reload()

  throw new Error("Reloading page")
}

export function lazyWithPreload<T extends ComponentType<unknown>>(
  factory: LazyFactory<T>,
): PreloadableComponent<T> {
  let result: Promise<{ default: T }> | undefined
  const load = () => {
    if (!result) {
      result = factory().catch((error) => {
        result = undefined
        if (isChunkLoadError(error)) {
          reloadIfStale()
        }
        throw error
      })
    }
    return result
  }
  const Component = lazy(load) as PreloadableComponent<T>
  Component.preload = load
  return Component
}
